import {
  Badge,
  Box, Flex, IconButton, Text
} from '@huspy/briks-web';
import { Menu } from '@huspy/briks-icons';
import { Image } from '@mantine/core';
import { imagesPath } from '@shared/css.const';
import React, {
  useEffect, useRef, MouseEventHandler
} from 'react';
import { Stack, HStack } from '@huspy/styled-system/jsx';
import { appShellRecipe } from '@recipes';
import {
  Link,
  useRouter,
} from '@tanstack/react-router';
import ScrollArea from '@components/v2/ScrollArea';
import { clearAmplitudeEventContext } from '@shared/analytics/amplitude';
import { useLayoutStore } from '@modules/core/store';
import { IS_SPAIN_ENV, IS_UAE_ENV } from '@modules/core/utils';
import useGetSidebarLinks from '@modules/core/layout/hooks/useGetSidebarLinks';
import '@mantine/core/styles/Drawer.layer.css';
import Tooltip from '@components/Tooltip';
import WaitingRoom from '@modules/identity/presentation/pages/waiting-room/features';
import { BuyCreditsSlido } from '@modules/payments/presentation/features/BuyCreditsSlido';
import SidebarProfile from '@modules/core/presentation/v2/layout/features/SidebarProfile';
import CreditsCTA from '@modules/core/presentation/v2/layout/features/CreditsCTA';
import { LegalAcceptanceForm } from '@modules/core/presentation/v2/layout/features/LegalAcceptanceForm';
import { CreditExpiredNotificationBanner } from '@modules/core/presentation/v2/layout/features/CreditExpiredNotificationBanner';
import { Breadcrumbs } from '@modules/core/presentation/v2/layout/features/Breadcrumbs';

type Props = {
  children: React.ReactNode;
  sidebarCollapsed: boolean;
  toggleCollapse(): void;
  sidebarLinks: ReturnType<typeof useGetSidebarLinks>['sidebarLinks'];
  hasAcceptedTnC: boolean;
  hasAccessToPortal: boolean | undefined;
};

const SidebarLinkItem = (
  {
    Icon, to, text, iconOnly = false, dataTest, onClick,
  }:
  {
    to: string;
    Icon: React.ReactNode;
    text: string;
    iconOnly: boolean;
    dataTest?: string;
    onClick?: MouseEventHandler<HTMLDivElement>;
  }
) => (
  <Link to={ to } data-test={ dataTest }>
    {(props) => (
      <HStack
        onClick={ onClick }
        _hover={ {
          boxShadow: 'shadow-2',
          borderColor: 'border-secondary',
          // eslint-disable-next-line sonarjs/no-duplicate-string
          color: 'text-primary',
        } }
        py='2,5'
        px='3'
        // eslint-disable-next-line max-len
        transition='box-shadow 0.3s ease-out, background-color 0.3s ease-out,box-shadow 0.3s ease-out, border 0.3s ease-out, color 0.3s ease-out'
        borderWidth='1px'
        borderColor='transparent'
        borderRadius='3'
        gap='3'
        color='text-secondary'
        { ...(iconOnly && { justifyContent: 'center' }) }
        { ...(props.isActive && {
          backgroundColor: 'bg-primary',
          color: 'text-primary',
          borderWidth: '1px',
          borderColor: 'border-secondary',
          boxShadow: 'shadow-2',
          fontWeight: 'medium',
        }) }
      >
        <Tooltip events={ { hover: iconOnly, focus: iconOnly, touch: iconOnly } } label={ text }>
          <Box>
            {Icon}
          </Box>
        </Tooltip>
        {!iconOnly && (
          <Text
            size='lg'
          >
            {text}
          </Text>
        )}
      </HStack>
    )}
  </Link>
);

export const LayoutV2 = ({
  children,
  sidebarCollapsed,
  toggleCollapse,
  sidebarLinks,
  hasAcceptedTnC,
  hasAccessToPortal,
}: Props) => {
  const isScrollToListenerAttached = useRef<null | true>(null);
  const viewport = useRef<HTMLDivElement>(null);

  const { asideContent, headerContent } = useLayoutStore();

  const appShellStyles = appShellRecipe({ collapsed: sidebarCollapsed, hasAsideContent: !!asideContent });
  const router = useRouter();

  // Restore scroll inside the scrollarea on router resolved event
  useEffect(() => {
    let unsub = () => {};
    if (!isScrollToListenerAttached.current && viewport) {
      unsub = router.subscribe('onResolved', ({ fromLocation, toLocation }) => {
        if (fromLocation.pathname !== toLocation.pathname) {
          viewport?.current?.scrollTo({ top: 0, behavior: 'instant' });
        }
      });
      isScrollToListenerAttached.current = true;
    }

    return () => unsub();
  }, [router, viewport, isScrollToListenerAttached]);

  // Clear Amplitude attribute context on every page
  useEffect(() => {
    const unsub = router.subscribe('onResolved', ({ fromLocation, toLocation }) => {
      if (fromLocation.pathname !== toLocation.pathname) {
        clearAmplitudeEventContext();
      }
    });
    return () => unsub();
  }, [router]);

  if (!hasAccessToPortal) {
    return (
      <Flex
        className={ appShellStyles.root }
        background='base.white'
        direction='column'
      >
        <Box
          bg='white'
          w='100%'
          height='200px'
          display='flex'
          justifyContent='center'
          alignItems='center'
        >
          <Image
            src={ `${imagesPath}/${IS_SPAIN_ENV ? 'bayteca-logo-dark.svg' : 'huspy+brokers.svg'}` }
            w='200px'
          />
        </Box>
        <WaitingRoom />
      </Flex>
    );
  }
  return (
    <Box
      className={ appShellStyles.root }
    >
      <BuyCreditsSlido />
      <Flex
        className={ appShellStyles.navbar }
        direction='column'
        justify='space-between'
      >
        <Stack gap='10'>
          <Flex justifyContent={ sidebarCollapsed ? 'center' : 'space-between' }>
            <Image
              style={ {
                transitionProperty: 'width',
                transitionDuration: '200ms',
                transitionTimingFunction: 'ease',
                ...(sidebarCollapsed && { width: 0 }),
              } }
              src={ `${imagesPath}/${IS_SPAIN_ENV ? 'bayteca-logo-dark.svg' : 'huspy+brokers.svg'}` }
            />
            <IconButton borderRadius='2' onClick={ () => toggleCollapse() } size='sm'>
              <Menu />
            </IconButton>
          </Flex>
          {sidebarLinks.map((category, idx) => (
            <Box key={ category.title ?? `item-${idx}` } gap='1' display='flex' flexDirection='column'>
              {category.title && !sidebarCollapsed && (
                <Text color='text-secondary' weight='semiBold' size='md' mb='1.5'>
                  {category.title}
                </Text>
              )}
              {category.links.map((link) => (
                <SidebarLinkItem
                  key={ link.id }
                  Icon={ link.svgIcon }
                  text={ link.label }
                  to={ link.to }
                  iconOnly={ sidebarCollapsed }
                  dataTest={ link.id }
                  onClick={ link.onClick }
                />
              ))}
            </Box>
          ))}
        </Stack>

        {typeof APP_GIT_COMMIT_HASH !== 'undefined' && (
          <Flex
            id='app_hash_version'
            justifyContent='center'
            style={ {
              visibility: ENV === 'production' ? 'hidden' : 'visible',
              position: 'absolute',
              bottom: '10px',
              cursor: 'pointer',
            } }
          >
            <a href={ `https://github.com/huspy/brokers/commit/${APP_GIT_COMMIT_HASH}` } target='_blank' rel='noreferrer'>
              <Badge variant='neutral'>
                {!sidebarCollapsed && (
                  <>
                    Version:
                    {' '}
                  </>
                )}
                {APP_GIT_COMMIT_HASH.slice(0, 7)}
              </Badge>
            </a>
          </Flex>
        )}
        <Flex justify='center'>
          <SidebarProfile collapsed={ sidebarCollapsed } />
        </Flex>
      </Flex>
      <Box
        className={ appShellStyles.mainWrapper }
      >
        <Box className={ appShellStyles.header }>
          <Breadcrumbs />
          <Flex align='center' gap={ 3 }>
            {headerContent}
            <CreditsCTA />
          </Flex>
        </Box>
        <Box>
          <CreditExpiredNotificationBanner />
        </Box>
        <Box className={ appShellStyles.main }>
          {asideContent ? (
            <>
              <Box className={ appShellStyles.aside } flexBasis='1/4'>
                <ScrollArea type='hover' w='100%'>
                  <Box px='6' py='8' id='asideContent'>
                    {asideContent}
                  </Box>
                </ScrollArea>
              </Box>
              <Flex width='100%' flexBasis='3/4' bg='utility-blue-50'>
                <ScrollArea
                  id='layout-v2-scrollable'
                  type='hover'
                  w='100%'
                  viewportRef={ viewport }
                >
                  <Box w='100%' py='8'>
                    <Box px='8' margin='0 auto'>
                      { children }
                    </Box>
                  </Box>
                </ScrollArea>
              </Flex>
            </>
          ) : (
            <ScrollArea
              type='hover'
              w='100%'
              viewportRef={ viewport }
            >
              <Box w='100%' px='6' py='6'>
                <Box maxWidth='1600px' margin='0 auto'>
                  { children }
                </Box>
              </Box>
            </ScrollArea>
          )}
        </Box>
      </Box>
      {IS_UAE_ENV && <LegalAcceptanceForm hasAcceptedTnC={ hasAcceptedTnC } />}
    </Box>
  );
};
